import { format } from 'date-fns';
import {
    EventAttendanceReportDto,
    EventAttendanceRowDto,
} from '../../../models/event/dto/event-attendance-report.dto';
import { EventStatus, EventType } from '../../../models/event/dto/event.dto';

export type ExportTranslate = (key: string) => string;

/**
 * Field delimiter — a semicolon, not a comma.
 *
 * Excel does not read a fixed separator: it splits a `.csv` on the system's
 * LIST SEPARATOR, which is `;` on French (and most European) Windows installs.
 * A comma-delimited file therefore opens as ONE unusable column on exactly the
 * machines this export is built for — the dashboard is French-only, `translate()`
 * resolves every key against the FR table and there is no locale switch
 * (src/i18n.ts), so there is no comma-Excel audience to trade off against.
 *
 * `;` is also why the file stays a CSV rather than growing an .xlsx writer
 * dependency: with the BOM below, French Excel opens it directly, columns and
 * accents intact.
 */
export const CSV_DELIMITER = ';';

/**
 * Quotes a cell when it holds the delimiter, a double quote, or a line break.
 * The rule is derived from `CSV_DELIMITER` rather than hard-coding a character,
 * so changing the delimiter cannot leave the escaping behind — a class named
 * "Ados; niveau 2" would otherwise split into two columns.
 *
 * A comma is plain data here and is deliberately left unquoted.
 */
function escapeCsvCell(value: string | number | undefined | null): string {
    if (value === undefined || value === null) return '';
    const s = String(value);
    if (s.includes(CSV_DELIMITER) || /["\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

/**
 * Fill rate as a whole-percent string. Uses a plain `%` suffix rather than a
 * locale-formatted number so Excel reads the column as text consistently —
 * a `0,375` that some locales produce and others don't is worse than a label.
 */
function formatFillRate(fillRate: number | null): string {
    if (fillRate === null || fillRate === undefined) return '';
    return `${Math.round(fillRate * 100)}%`;
}

/**
 * Attendance is tri-state and the CSV must keep it that way: a number when
 * pointage recorded check-ins, and an empty cell when the session had no
 * pointage at all. Writing 0 for "no pointage" would tell the club nobody
 * came to a class that may well have been full.
 */
function formatAttended(attendedCount: number | null): string {
    if (attendedCount === null || attendedCount === undefined) return '';
    return String(attendedCount);
}

function statusLabel(status: EventStatus, t: ExportTranslate): string {
    switch (status) {
        case EventStatus.CANCELLED:
            return t('event.status.cancelled');
        case EventStatus.COMPLETED:
            return t('event.status.completed');
        case EventStatus.ACTIVE:
            return t('event.status.active');
        default:
            return String(status);
    }
}

function typeLabel(eventType: EventType, t: ExportTranslate): string {
    switch (eventType) {
        case EventType.CLASS:
            return t('event.attendance_export.type_class');
        case EventType.COMPETITION:
            return t('event.attendance_export.type_competition');
        case EventType.OFFICIAL_COMPETITION:
            return t('event.attendance_export.type_official_competition');
        case EventType.OTHER:
            return t('event.attendance_export.type_other');
        default:
            return String(eventType);
    }
}

function attendanceRow(row: EventAttendanceRowDto, t: ExportTranslate): string[] {
    const start = new Date(row.startTime);
    const end = new Date(row.endTime);
    return [
        format(start, 'dd/MM/yyyy'),
        format(start, 'HH:mm'),
        format(end, 'HH:mm'),
        row.name,
        row.coachName ?? '',
        row.capacity,
        row.registeredCount,
        formatFillRate(row.fillRate),
        formatAttended(row.attendedCount),
        statusLabel(row.status, t),
        typeLabel(row.eventType, t),
    ].map((value) => escapeCsvCell(value as string | number));
}

/**
 * Builds the group-class occupancy CSV — one line per session, in the order
 * the API returned them (chronological, all class names mixed).
 *
 * Column order follows the request: date, times, class name, coach, capacity,
 * registered, fill rate, attendees. Status and type are appended after those,
 * so the requested columns keep their positions while a cancelled session is
 * still distinguishable from a held one.
 */
export function buildEventAttendanceCsv(
    report: EventAttendanceReportDto,
    translate: ExportTranslate
): string {
    const t = (k: string) => translate(k) || k;
    const headers = [
        t('event.attendance_export.col_date'),
        t('event.attendance_export.col_start_time'),
        t('event.attendance_export.col_end_time'),
        t('event.attendance_export.col_name'),
        t('event.attendance_export.col_coach'),
        t('event.attendance_export.col_capacity'),
        t('event.attendance_export.col_registered'),
        t('event.attendance_export.col_fill_rate'),
        t('event.attendance_export.col_attended'),
        t('event.attendance_export.col_status'),
        t('event.attendance_export.col_type'),
    ];

    const headerRow = headers.map(escapeCsvCell).join(CSV_DELIMITER);
    const dataRows = (report.rows ?? []).map((row) =>
        attendanceRow(row, t).join(CSV_DELIMITER)
    );
    return [headerRow, ...dataRows].join('\r\n');
}

/** `cours-collectifs_2026-01-01_2026-01-31` — no extension, downloadCsv adds it. */
export function buildAttendanceExportFilename(from: string, to: string): string {
    return `cours-collectifs_${from}_${to}`;
}

/**
 * How long the object URL is kept alive after the click. The browser reads the
 * blob asynchronously, so revoking in the same tick can cancel or truncate the
 * download it was meant to clean up after.
 */
const OBJECT_URL_TTL_MS = 1000;

/**
 * Triggers a file download of the given CSV content.
 *
 * Three details, each of which silently breaks the download when missing:
 *  - the leading BOM is what makes Excel read the file as UTF-8 instead of
 *    mangling every accented class and coach name;
 *  - the anchor is attached to the document before it is clicked \u2014 a click on
 *    a detached anchor is ignored by some browsers, and the download then just
 *    never starts, with no error anywhere;
 *  - the object URL is revoked on a timer rather than synchronously after the
 *    click, which would race the browser's own read of the blob.
 */
export function downloadCsv(csvContent: string, filename: string): void {
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
}
